import {
  GenericElement,
  SelectableTable,
  WbChip,
  WbDivider,
  WbMultiSelectionPicker,
  WbNoData,
  WbButton,
  WbUsersAndGroupsPicker,
} from '@agilelab/plugin-wb-platform';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Box, Switch, Typography, makeStyles } from '@material-ui/core';
import { WbTooltip } from '@agilelab/plugin-wb-platform';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useAssignmentsContext } from '../AssignmentsContext';
import { EntityKindSelector } from './EntityKindSelector';
import { useManageAssignmentsDrawerContext } from './ManageAssignmentsDrawerContext';
import { entityHandler } from './scopeProvider.factory';
import { AssignmentFormType, MyProvider, ProviderFilters } from './types';

const useStyles = makeStyles(
  theme => ({
    form: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    flexBox: {
      display: 'flex',
      gap: theme.spacing(0.5),
    },
    formContent: {
      display: 'flex',
      flex: 1,
      gap: theme.spacing(1),
      padding: theme.spacing(1),
      overflow: 'auto',
    },
    actions: {
      display: 'flex',
      padding: theme.spacing(1),
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      gap: theme.spacing(1),
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
    },
  }),
  { name: 'AssignmentForm' },
);

const SelectScopeHeader = ({
  onChange,
  selectionDisabled,
}: {
  onChange: () => void;
  selectionDisabled?: boolean;
}) => {
  const classes = useStyles();
  return (
    <Box
      className={classes.flexBox}
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
    >
      <Typography variant="h6" style={{ fontWeight: '500' }}>
        Select scope*
      </Typography>
      <Box className={classes.flexBox} flexDirection="row" alignItems="center">
        <WbTooltip title="This option selects all current scopes and any future updates or changes.">
          <span>
            <Typography variant="caption">
              Apply <WbChip style={{ marginRight: '4px' }} label="*All*" />
              scope
            </Typography>
          </span>
        </WbTooltip>
        <Switch
          size="small"
          color="primary"
          checked={selectionDisabled}
          onChange={onChange}
          name="applyAll"
        />
        <WbDivider />
        <EntityKindSelector disabled={selectionDisabled} />
      </Box>
    </Box>
  );
};

export const MultipleSelectionTable = ({
  selectedKind,
  providerMapper,
}: {
  selectedKind: string;
  providerMapper: Map<string, MyProvider<GenericElement, ProviderFilters>>;
}) => {
  const provider = providerMapper.get(selectedKind);
  const context = provider?.useContext();

  const handleChangePage = (newPage: number) => {
    context?.setPagination(v => ({
      limit: v.limit ?? 0,
      currentPage: newPage,
      cursor:
        context?.pagination.currentPage > newPage
          ? context.prevCursor
          : context.nextCursor,
    }));
  };

  const handleChangeRowsPerPage = (limit: number) => {
    context?.setPagination({
      limit: limit,
      currentPage: 0,
      cursor: undefined,
    });
  };

  return (
    <SelectableTable
      getRowId={context?.getRowId!}
      columns={context?.columns ?? []}
      loading={context?.entitiesState.loading ?? false}
      data={context?.entitiesState.value ?? []}
      filtersSection={provider?.filtersSection!}
      kindFilter={selectedKind}
      pagination={
        context?.pagination && {
          countlessOptions: context?.pagination.countlessOptions,
          rowsPerPageOptions: [25, 50, 100],
          count: context?.count,
          limit: context?.pagination.limit ?? 0,
          currentPage: context?.pagination.currentPage,
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
        }
      }
    />
  );
};

export const AssignmentForm: FC = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const { setOpenManageDrawer, assignmentsLoaded } = useAssignmentsContext();
  const {
    formValue,
    selectedScopes,
    setSelectedScopes,
    selectedKind,
    addAssignments,
    syncUserScopes,
  } = useManageAssignmentsDrawerContext();

  const { control, handleSubmit, watch, setValue } =
    useForm<AssignmentFormType>({
      defaultValues: formValue,
    });

  const [isAllSelected, setIsAllSelected] = useState(() =>
    Boolean(formValue?.scopes?.[0] === '*'),
  );

  const [resolvedEntities, setResolvedEntities] = useState<GenericElement[]>(
    [],
  );

  const providerMapper = useMemo(() => entityHandler(catalogApi), [catalogApi]);

  const entityProv = useMemo(() => {
    return providerMapper.get(selectedKind);
  }, [providerMapper, selectedKind]);

  const renderedComponent = entityProv?.renderContextProvider(
    <MultipleSelectionTable
      selectedKind={selectedKind}
      providerMapper={providerMapper}
    />,
  );

  const onSubmit = (data: AssignmentFormType) => {
    if (!!formValue?.users?.length) {
      syncUserScopes(data);
    } else {
      addAssignments(data);
    }
  };

  const fetchData = useCallback(
    async (ref: string) => {
      for (const [_, provider] of Array.from(providerMapper.entries())) {
        try {
          const res = await provider.resolve(ref);
          if (res) return res;
        } catch (err) {
          return undefined;
        }
      }
      return undefined;
    },
    [providerMapper],
  );

  const resolveEntitiesByFormData = useCallback(
    async (data: string[]): Promise<GenericElement[]> => {
      return Promise.all(
        data.map(async input => {
          const entity = await fetchData(input);
          return entity
            ? entity
            : {
                id: input,
                displayName: input,
                name: input,
              };
        }),
      );
    },
    [fetchData],
  );

  useEffect(() => {
    const fetchScopes = async () => {
      if (formValue?.scopes && formValue.scopes[0] !== '*') {
        const resolved = await resolveEntitiesByFormData(formValue.scopes);
        setResolvedEntities(resolved);
        setSelectedScopes(resolved);
        setIsAllSelected(false);
      } else if (formValue?.scopes?.[0] === '*') {
        setSelectedScopes([{ id: '*', displayName: '*All*' }]);
        setIsAllSelected(true);
      }
    };

    fetchScopes();
  }, [formValue, resolveEntitiesByFormData, setSelectedScopes]);

  const toggleAllSelection = useCallback(() => {
    if (isAllSelected) {
      setIsAllSelected(false);

      if (resolvedEntities.length > 0) {
        setSelectedScopes(resolvedEntities);
      } else {
        const initialScopes =
          watch('scopes')?.filter(scope => scope !== '*') || [];

        if (initialScopes.length > 0) {
          (async () => {
            const resolved = await resolveEntitiesByFormData(initialScopes);
            setResolvedEntities(resolved);
            setSelectedScopes(resolved);
            setValue('scopes', initialScopes);
          })();
        } else {
          setSelectedScopes([]);
          setValue('scopes', []);
        }
      }
    } else {
      setIsAllSelected(true);
      setSelectedScopes([{ id: '*', displayName: '*All*' }]);
      setValue('scopes', ['*']);
    }
  }, [
    isAllSelected,
    resolveEntitiesByFormData,
    resolvedEntities,
    setSelectedScopes,
    setValue,
    watch,
  ]);

  useEffect(() => {
    if (
      !isAllSelected &&
      selectedScopes &&
      selectedScopes.length > 0 &&
      selectedScopes[0].id !== '*'
    ) {
      setResolvedEntities(selectedScopes);
    }
  }, [isAllSelected, selectedScopes]);

  useEffect(() => {
    if (selectedScopes && selectedScopes.length === 0) {
      setValue('scopes', ['*']);
    }
  }, [selectedScopes, setValue]);

  const checkOptionDisabled = (option: string) => {
    return (
      assignmentsLoaded.value?.some(el => el.userOrGroup.urn === option) ??
      false
    );
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <Box className={classes.formContent} flexDirection="column">
        <Typography variant="h6" style={{ fontWeight: '500' }}>
          Select Users and Groups*
        </Typography>
        <Controller
          name="users"
          control={control}
          render={({ field }) => (
            <WbUsersAndGroupsPicker
              disabled={!!formValue?.users?.length}
              value={field.value ?? []}
              onChange={field.onChange}
              checkOptionDisabled={checkOptionDisabled}
            />
          )}
        />
        <Controller
          name="scopes"
          control={control}
          render={({ field }) => (
            <WbMultiSelectionPicker
              selectedValues={selectedScopes}
              setSelectedValues={setSelectedScopes}
              contentHeader={
                <SelectScopeHeader
                  onChange={toggleAllSelection}
                  selectionDisabled={isAllSelected}
                />
              }
              disableAll={isAllSelected}
              onChange={(values: GenericElement[]) => {
                if (values.length === 0 && isAllSelected) {
                  field.onChange(['*']);
                } else {
                  field.onChange(values.map(v => v.id));
                }
              }}
            >
              {renderedComponent ?? <WbNoData />}
            </WbMultiSelectionPicker>
          )}
        />
      </Box>
      <Box className={classes.actions}>
        <WbButton
          onClick={() => {
            setOpenManageDrawer(false);
          }}
          variant="outlined"
          color="primary"
          label="Cancel"
        />
        <WbButton
          label="Assign"
          type="submit"
          disabled={!watch('scopes')?.length || !watch('users')?.length}
          variant="contained"
          color="primary"
        />
      </Box>
    </form>
  );
};
